import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

import SuccessToast from '../../components/ui/SuccessToast';

const RentalPricingPanelScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editingPricing, setEditingPricing] = useState({});

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('vehicles')
        ?.select(`*, vehicle_pricing(*), user_profiles!vehicles_owner_id_fkey(full_name)`)
        ?.in('status', ['active', 'pending_review'])
        ?.order('created_at', { ascending: false });
      if (!error) {
        setVehicles(data || []);
        const pricingMap = {};
        data?.forEach(v => {
          const p = v?.vehicle_pricing?.[0] || {};
          pricingMap[v?.id] = {
            base_daily_rate: p?.base_daily_rate || 0,
            weekend_multiplier: p?.weekend_multiplier || 1.0,
            chauffeur_daily_addon: p?.chauffeur_daily_addon || 0,
            deposit_amount: p?.deposit_amount || 0,
            min_days: p?.min_days || 1,
            mileage_limit_km_per_day: p?.mileage_limit_km_per_day || 200,
            overage_fee_per_km: p?.overage_fee_per_km || 0,
          };
        });
        setEditingPricing(pricingMap);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSavePricing = async (vehicleId) => {
    setSaving(vehicleId);
    try {
      const p = editingPricing?.[vehicleId];
      const { data, error } = await supabase?.rpc('update_vehicle_pricing', {
        p_vehicle_id: vehicleId,
        p_admin_id: user?.id,
        p_base_daily_rate: parseFloat(p?.base_daily_rate),
        p_weekend_multiplier: parseFloat(p?.weekend_multiplier),
        p_chauffeur_daily_addon: parseFloat(p?.chauffeur_daily_addon),
        p_deposit_amount: parseFloat(p?.deposit_amount),
        p_min_days: parseInt(p?.min_days),
        p_mileage_limit_km_per_day: parseInt(p?.mileage_limit_km_per_day),
        p_overage_fee_per_km: parseFloat(p?.overage_fee_per_km),
      });
      if (error) throw error;
      if (data?.success) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      }
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const updateField = (vehicleId, field, value) => {
    setEditingPricing(prev => ({ ...prev, [vehicleId]: { ...prev?.[vehicleId], [field]: value } }));
  };

  const inputStyle = { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' };

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {showSuccess && <SuccessToast message="Pricing updated successfully!" visible={showSuccess} subMessage="" onDismiss={() => setShowSuccess(false)} />}
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/admin-dashboard')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="text-white" />
          </button>
          <div>
            <h1 className="text-white font-bold text-lg">Rental Pricing Control Panel</h1>
            <p className="text-white/40 text-xs">Manage vehicle pricing, deposits, and mileage rules</p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3]?.map(i => <div key={i} className="h-48 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}
          </div>
        ) : vehicles?.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-3">💰</div>
            <h3 className="text-white font-bold text-lg mb-1">No vehicles to price</h3>
            <p className="text-white/40 text-sm">Approve vehicle listings first</p>
          </div>
        ) : (
          <div className="space-y-6">
            {vehicles?.map(vehicle => {
              const p = editingPricing?.[vehicle?.id] || {};
              const isSaving = saving === vehicle?.id;
              return (
                <div key={vehicle?.id} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-10 rounded-xl bg-white/10 flex items-center justify-center text-xl">🚗</div>
                      <div>
                        <h3 className="text-white font-bold text-base">{vehicle?.title}</h3>
                        <p className="text-white/40 text-xs">Owner: {vehicle?.user_profiles?.full_name || 'N/A'} · {vehicle?.rental_mode?.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${vehicle?.status === 'active' ? 'text-[#34d399]' : 'text-[#fbbf24]'}`} style={{ background: vehicle?.status === 'active' ? 'rgba(52,211,153,0.12)' : 'rgba(251,191,36,0.12)' }}>
                      {vehicle?.status === 'active' ? 'Active' : 'Pending'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-5">
                    {[
                      { key: 'base_daily_rate', label: 'Base Daily Rate (KES)', type: 'number' },
                      { key: 'weekend_multiplier', label: 'Weekend Multiplier', type: 'number', step: '0.1' },
                      { key: 'chauffeur_daily_addon', label: 'Chauffeur Add-on (KES)', type: 'number' },
                      { key: 'deposit_amount', label: 'Deposit Amount (KES)', type: 'number' },
                      { key: 'min_days', label: 'Min Rental Days', type: 'number' },
                      { key: 'mileage_limit_km_per_day', label: 'Daily Mileage Limit (km)', type: 'number' },
                      { key: 'overage_fee_per_km', label: 'Overage Fee (KES/km)', type: 'number' },
                    ]?.map(field => (
                      <div key={field?.key}>
                        <label className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">{field?.label}</label>
                        <input
                          type={field?.type}
                          step={field?.step || '1'}
                          value={p?.[field?.key] || ''}
                          onChange={e => updateField(vehicle?.id, field?.key, e?.target?.value)}
                          className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40"
                          style={inputStyle}
                        />
                      </div>
                    ))}
                  </div>
                  <button onClick={() => handleSavePricing(vehicle?.id)} disabled={isSaving} className="btn-primary text-sm py-2.5 px-6">
                    {isSaving ? 'Saving...' : <><Icon name="Save" size={16} /> Save Pricing</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalPricingPanelScreen;
